import { useEffect } from 'react'
import { v1Client } from '@renderer/chat/api'
import { useAuth } from '@renderer/chat/providers'
import {
  beginTurn,
  endTurn,
  getActiveStreamingChatIds,
  getTurnStartTime,
  isTurnLive,
  isTurnRegistered,
  useActiveStreamingChatIds,
} from '@renderer/chat/store'
import { useSidepanelState } from '../atoms'

const VERIFY_INTERVAL_MS = 15_000

// A just-started stream may not be marked isStreaming on the backend yet; a
// stale-false verify would clobber the fresh submit's progress cell. Skip
// entries younger than this (start time is set at submit/subscribe).
const FRESH_STREAM_GRACE_MS = 10_000

/**
 * Finalizes streaming chats that no mounted view owns.
 *
 * Closing the sidepanel mid-stream aborts the chat's `/subscribe` SSE, which
 * deliberately leaves the chat's turn live for a future drawer open to
 * reconcile (the engine resolves the abort as `interrupted` and skips
 * `endTurn`). But while the panel stays closed nothing reconciles, so the
 * chat never reaches 'completed' and the chat-complete toast never fires
 * (HIG-6036). This always-mounted watcher runs the same backend verify as
 * useSyncStreamingState for every live turn no stream owns and records its
 * completion — which is what useChatCompletionToasts consumes.
 */
export function useDetachedChatCompletionWatcher() {
  const { getAuthToken } = useAuth()
  const streamingIds = useActiveStreamingChatIds()
  // Dep only: the sidepanel closing is the moment a subscription was just
  // aborted, so re-running then verifies immediately instead of up to one
  // interval later.
  const sidepanelState = useSidepanelState()

  useEffect(() => {
    if (streamingIds.length === 0) return
    let cancelled = false

    const verify = async () => {
      for (const id of getActiveStreamingChatIds()) {
        if (cancelled) return
        if (isTurnRegistered(id)) continue
        const start = getTurnStartTime(id)
        if (start && Date.now() - start < FRESH_STREAM_GRACE_MS) continue
        try {
          const token = await getAuthToken()
          const response = await v1Client.GET('/conversations/{id}', {
            params: { path: { id } },
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          })
          const data = response.data?.data
          // No data (404 / temp id / auth hiccup) is not evidence the stream
          // ended — never finalize on it.
          if (cancelled || !data || data.isStreaming) continue
          // A fresh submit may have re-registered the chat mid-request.
          if (!isTurnLive(id) || isTurnRegistered(id)) continue
          // Backfill the title so endTurn records the completion (the store
          // only keeps titled turns) and the toast has a label.
          // `||` not `??`: an empty title is stored as-is by the setter and
          // would make endTurn drop the completion silently.
          beginTurn(id, { title: data.title || 'Chat' })
          endTurn(id)
        } catch {
          // Transient (network/auth); the next tick retries.
        }
      }
    }

    void verify()
    const interval = setInterval(() => void verify(), VERIFY_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [streamingIds, sidepanelState, getAuthToken])
}
